import { createFileRoute, Link } from "@tanstack/react-router";
import { Phone, MessageCircle, Mail, MapPin, Clock, Globe2 } from "lucide-react";
import { LeadFormModal } from "@/components/LeadFormModal";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { RelatedHubGrid } from "@/components/RelatedHubGrid";

const SITE = "https://nyclanguageinstitute.com";
const TITLE = "Contact NYC Language Institute — Bangkok Office, LINE, Phone, Email";
const DESC =
  "Reach NYC Legal in 15 minutes. Bangkok HQ (Ladprao 95), phone +66 83 249 4999, LINE @nycli. Ask us anything — real experts reply within minutes.";

export const Route = createFileRoute("/en/contact")({
  head: () => ({
    meta: [
      { title: ctaTitle(TITLE, "/en/contact", "en")},
      { name: "description", content: ctaDesc(DESC, "/en/contact", "en")},
      { property: "og:title", content: ctaTitle(TITLE, "/en/contact", "en")},
      { property: "og:description", content: ctaDesc(DESC, "/en/contact", "en")},
      { property: "og:url", content: `${SITE}/en/contact` },
      { property: "og:locale", content: "en_US" },
    ],
    links: [
      { rel: "canonical", href: `${SITE}/en/contact` },
      ...hreflangLinks("/quote", "/en/contact"),
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: TITLE,
          url: `${SITE}/en/contact`,
          inLanguage: "en",
          mainEntity: {
            "@type": "ProfessionalService",
            name: "NYC Legal & Notary Service Co., Ltd.",
            telephone: "+66-83-249-4999",
            email: "contact@nyclegal.co.th",
            address: {
              "@type": "PostalAddress",
              streetAddress: "61 Soi Ladprao 95 (Prangthip), Wang Thonglang",
              addressLocality: "Bangkok",
              postalCode: "10310",
              addressCountry: "TH",
            },
            openingHours: "Mo-Sa 09:00-18:00",
          },
        }),
      },
    ],
  }),
  component: EnContactPage,
});

function EnContactPage() {
  return (
    <main className="min-h-screen bg-navy-deep text-white">
      <section className="border-b border-white/10 bg-gradient-to-b from-[#0a1a3d] to-[#0d2150] px-4 py-14">
        <div className="mx-auto max-w-5xl">
          <Link to="/en" className="text-xs text-amber-300 hover:underline">← EN home</Link>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">
            Talk to us in <span className="bg-gradient-to-r from-amber-300 to-amber-500 bg-clip-text text-transparent">15 minutes</span>
          </h1>
          <p className="mt-4 max-w-2xl text-white/80">
            Pick the channel that works for you. All conversations handled by licensed staff, never bots.
          </p>
        </div>
      </section>

      <section className="px-4 py-12">
        <div className="mx-auto grid max-w-5xl gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {[
            { icon: Phone, title: "Phone", lines: ["+66 83 249 4999", "Mon–Sat 09:00–18:00 ICT"], href: "tel:+66832494999", cta: "Call now", color: "bg-amber-500 text-navy-deep" },
            { icon: MessageCircle, title: "LINE", lines: ["@nycli", "24/7 message · reply within 15 min"], href: "https://line.me/ti/p/@nycli", cta: "Open LINE", color: "bg-[#04A03A] text-white" },
            { icon: Mail, title: "Email", lines: ["contact@nyclegal.co.th", "Quotes confirmed in writing"], href: "mailto:contact@nyclegal.co.th", cta: "Send email", color: "bg-white text-navy-deep" },
          ].map((c) => (
            <article key={c.title} className="rounded-2xl border border-white/10 bg-white/[0.04] p-6">
              <c.icon className="h-7 w-7 text-amber-300" />
              <h2 className="mt-3 text-base font-bold">{c.title}</h2>
              {c.lines.map((l) => <p key={l} className="mt-1 text-sm text-white/70">{l}</p>)}
              <a href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className={`mt-4 inline-flex items-center gap-2 rounded-lg px-4 py-2 text-xs font-bold ${c.color}`}>{c.cta}</a>
            </article>
          ))}
        </div>

        <div className="mx-auto mt-10 max-w-5xl rounded-2xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
          <div className="grid gap-6 sm:grid-cols-2">
            <div>
              <div className="flex items-center gap-2 text-amber-300"><MapPin className="h-5 w-5" /><h3 className="text-base font-bold">Bangkok Headquarters</h3></div>
              <p className="mt-3 text-sm text-white/80">
                61 Soi Ladprao 95 (Prangthip)<br />
                Khlong Chao Khun Sing, Wang Thonglang<br />
                Bangkok 10310, Thailand
              </p>
              <a href="https://maps.google.com/?q=NYC+Legal+Notary+Service+Bangkok" target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-amber-300 hover:underline">
                Open in Google Maps →
              </a>
            </div>
            <div>
              <div className="flex items-center gap-2 text-amber-300"><Clock className="h-5 w-5" /><h3 className="text-base font-bold">Service Hours</h3></div>
              <p className="mt-3 text-sm text-white/80">
                Mon–Fri 09:00–18:00 ICT<br />
                Saturday 09:00–17:00 ICT<br />
                Sunday & Holidays: LINE on-call
              </p>
              <div className="mt-3 flex items-center gap-2 text-amber-300"><Globe2 className="h-5 w-5" /><h3 className="text-base font-bold">Coverage</h3></div>
              <p className="mt-2 text-sm text-white/80">Bangkok HQ · 50 districts · 76 provinces · international DHL/FedEx</p>
            </div>
          </div>

          <div className="mt-8 border-t border-white/10 pt-6 text-center">
            <LeadFormModal trigger={
              <button type="button" className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-6 py-3 text-sm font-bold text-navy-deep hover:bg-amber-400">
                <Mail className="h-4 w-4" /> Request Custom Quote
              </button>
            } />
          </div>
        <RelatedHubGrid locale="en" />
      </div>
      </section>
    </main>
  );
}
